import { apiClient } from '@/utils/api-client';
import type { ApiResponse, JsonApiResource } from '@/types/api';

// Admin user type based on Rails model
export interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  name: string;
  role: 'super' | 'admin' | 'data' | 'affiliate' | 'affiliate_admin';
  status: 'active' | 'inactive';
  auth0_sub?: string;
  is_super: boolean;
  affiliate_id?: string;
  affiliate_name?: string;
  created_at: string;
  updated_at: string;
}

export interface AdminListParams {
  q?: string;              // Search query
  include_inactive?: boolean;
  limit?: number;
  skip?: number;
}

export interface AdminCreateData {
  email: string;
  first_name: string;
  last_name: string;
  role: AdminUser['role'];
  auth0_sub?: string;
  affiliate_id?: string;
}

export interface AdminUpdateData {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: AdminUser['role'];
  status?: 'active' | 'inactive';
  affiliate_id?: string;
}

// Admin service for managing admin users
export const adminService = {
  // List all admin users with filtering and pagination
  async listAdmins(params?: AdminListParams): Promise<{
    data: JsonApiResource<AdminUser>[];
    meta: {
      total_count: number;
      limit: number;
      skip: number;
    };
  }> {
    const queryParams: Record<string, any> = {};
    
    if (params?.q) queryParams.q = params.q;
    if (params?.include_inactive) queryParams.include_inactive = 'true';
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.skip) queryParams.skip = params.skip;
    
    return apiClient.get('/api/admin/admin_users', queryParams);
  },

  // Get specific admin by ID
  async getAdmin(id: string): Promise<ApiResponse<JsonApiResource<AdminUser>>> {
    return apiClient.get(`/api/admin/admin_users/${id}`);
  },

  // Create new admin user
  async createAdmin(data: AdminCreateData): Promise<ApiResponse<JsonApiResource<AdminUser>>> {
    return apiClient.post('/api/admin/admin_users', {
      data: {
        type: 'admin',
        attributes: data
      }
    });
  },

  // Update admin user
  async updateAdmin(id: string, data: AdminUpdateData): Promise<ApiResponse<JsonApiResource<AdminUser>>> {
    return apiClient.patch(`/api/admin/admin_users/${id}`, {
      data: {
        type: 'admin',
        attributes: data
      }
    });
  },

  // Delete admin user (soft delete)
  async deleteAdmin(id: string): Promise<void> {
    return apiClient.delete(`/api/admin/admin_users/${id}`);
  },

  // Send password reset email to an admin user
  async sendPasswordReset(id: string): Promise<{ message: string }> {
    return apiClient.post(`/api/admin/admin_users/${id}/send_password_reset`, {});
  },

  // Helper function to get role display name
  getRoleDisplayName(role: AdminUser['role']): string {
    const roleMap: Record<AdminUser['role'], string> = {
      'super': 'Super Admin',
      'admin': 'Administrative User',
      'data': 'Customer Editor',
      'affiliate': 'Affiliate',
      'affiliate_admin': 'Affiliate + Admin'
    };
    
    return roleMap[role] || role;
  },

  // Check if current user can edit another user
  canEditUser(currentUser: AdminUser | null, targetUser: AdminUser): boolean {
    if (!currentUser) return false;
    
    // Super admins can edit anyone
    if (currentUser.is_super) return true;
    
    // Regular admins cannot edit super admins
    if (targetUser.is_super && !currentUser.is_super) return false;
    
    // Otherwise, admins can edit other non-super users
    return true;
  }
};